import { UseMutationOptions, useQuery, useMutation, useInfiniteQuery } from 'react-query';

import {
  CreateEntityTagRequest,
  DeleteEntityTagRequest,
  ListTagsRequest,
  ReportEntityTagHistoryRequest,
  ReportEntityTagsStateRequest,
  ReportEntityTagStateRequest,
} from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';

import {
  reportEntityTagsState,
  createEntityTag,
  deleteEntityTag,
  reportEntityTagState,
  reportEntityTagHistory,
  getTags,
} from './api';

type UseListTagsProps = {
  namespaceId?: string;
};

export const useListTagsQuery = ({ namespaceId }: UseListTagsProps = {}) => {
  return useQuery({
    queryKey: [namespaceId, 'ListTagsRequest'],
    queryFn: async () => {
      const payload: ListTagsRequest = {
        namespaceId: namespaceId,
      };
      return getTags(payload);
    },
  });
};

type UseReportEntityTagsStateProps = {
  namespaceId?: string;
  entityId?: string;
};

export const useReportEntityTagsStateQuery = ({
  namespaceId,
  entityId,
}: UseReportEntityTagsStateProps = {}) => {
  return useQuery({
    queryKey: [namespaceId, entityId, 'ReportEntityTagsStateRequest'],
    queryFn: async () => {
      const payload: ReportEntityTagsStateRequest = {
        namespaceId: namespaceId,
        entityId: entityId,
      };
      return reportEntityTagsState(payload);
    },
    refetchOnWindowFocus: false,
  });
};

type UseReportEntityTagStateProps = {
  namespaceId?: string;
  tagId?: string;
  entityId?: string;
};

export const useReportEntityTagStateQuery = ({
  namespaceId,
  tagId,
  entityId,
}: UseReportEntityTagStateProps = {}) => {
  return useQuery({
    queryKey: [namespaceId, tagId, entityId, 'ReportEntityTagStateRequest'],
    queryFn: async () => {
      const payload: ReportEntityTagStateRequest = {
        namespaceId: namespaceId,
        tagId: tagId,
        entityId: entityId,
      };
      return reportEntityTagState(payload);
    },
    refetchOnWindowFocus: false,
  });
};

type UseReportEntityTagHistoryProps = {
  namespaceId?: string;
  tagId?: string;
  entityId?: string;
  enabled?: boolean;
};

export const useReportEntityTagHistoryQuery = ({
  namespaceId,
  tagId,
  entityId,
  enabled = true,
}: UseReportEntityTagHistoryProps = {}) => {
  return useQuery({
    queryKey: [namespaceId, tagId, entityId, 'ReportEntityTagHistoryRequest'],
    queryFn: async () => {
      const payload: ReportEntityTagHistoryRequest = {
        namespaceId: namespaceId,
        tagId: tagId,
        entityId: entityId,
      };
      return reportEntityTagHistory(payload);
    },
    refetchOnWindowFocus: 'always', // todo: how to manually retrigger?
    enabled: enabled && !!namespaceId && !!tagId && !!entityId,
  });
};

export const useInfiniteReportEntityTagHistoryQuery = ({
  namespaceId,
  tagId,
  entityId,
  format,
  enabled,
}: Pick<
  ReportEntityTagHistoryRequest,
  'namespaceId' | 'tagId' | 'entityId' | 'format'
> & {
  enabled: boolean;
}) =>
  useInfiniteQuery(
    ['ReportEntityTagHistoryRequest', namespaceId, tagId, entityId, format, 'infinite'],
    ({ pageParam = '' }) =>
      reportEntityTagHistory({
        namespaceId,
        tagId,
        entityId,
        format,
        pageSize: 50,
        pageToken: pageParam,
      }),
    {
      enabled: enabled && !!namespaceId && !!tagId && !!entityId,
      getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: (error) => console.error('Error fetching entity tag history:', error),
    }
  );

export const useCreateEntityTagMutation = (
  options?: UseMutationOptions<void, Error, CreateEntityTagRequest>
) => {
  return useMutation(
    ['CreateEntityTagRequest'],
    (req: CreateEntityTagRequest) => createEntityTag(req),
    options
  );
};

export const useDeleteEntityTagMutation = (
  options?: UseMutationOptions<void, Error, DeleteEntityTagRequest>
) => {
  return useMutation(
    ['DeleteEntityTagRequest'],
    (req: DeleteEntityTagRequest) => deleteEntityTag(req),
    options
  );
};
